import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useWebSocket } from 'global/WebSocketContext';
import { useUserId } from 'hooks/useCurrentUser';
import { useNotificationPreferences } from 'hooks/useNotificationPreferences';
import { apiNotifications } from 'services/apiService';
import { Notification, NotificationCategory, NotificationPriority } from 'types/notificationsTypes';

export type NotificationGroup = 'today' | 'yesterday' | 'thisWeek' | 'older';

export interface GroupedNotifications {
  today: Notification[];
  yesterday: Notification[];
  thisWeek: Notification[];
  older: Notification[];
}

interface NotificationsContextProps {
  notifications: Notification[];
  groupedNotifications: GroupedNotifications;
  markAsRead: (ids: string | string[]) => void;
  markAllAsRead: () => void;
  markAsSeen: (ids: string | string[]) => void;
  markAllAsSeen: () => void;
  unreadCount: number;
  unseenCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isDNDActive: boolean;
}

const NotificationsContext = createContext<NotificationsContextProps | undefined>(undefined);

// Helper to get date at start of day for grouping
const getStartOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper to check if date is today
const isToday = (date: Date): boolean => {
  const today = getStartOfDay(new Date());
  const targetDate = getStartOfDay(date);
  return today.getTime() === targetDate.getTime();
};

// Helper to check if date is yesterday
const isYesterday = (date: Date): boolean => {
  const yesterday = getStartOfDay(new Date());
  yesterday.setDate(yesterday.getDate() - 1);
  const targetDate = getStartOfDay(date);
  return yesterday.getTime() === targetDate.getTime();
};

// Helper to check if date is within this week (last 7 days)
const isThisWeek = (date: Date): boolean => {
  const now = new Date();
  const weekAgo = new Date(now);
  weekAgo.setDate(weekAgo.getDate() - 7);
  return date >= weekAgo && !isToday(date) && !isYesterday(date);
};

interface NotificationsProviderProps {
  children: ReactNode;
}

interface NotificationsApiResponse {
  items: Notification[];
  total: number | null;
  page: number | null;
  size: number | null;
  pages?: number | null;
}

// Helper to map notification origin to category
const getCategoryFromOrigin = (origin?: string): NotificationCategory | null => {
  if (!origin) return null;
  const lowerOrigin = origin.toLowerCase();

  if (lowerOrigin.includes('meeting') || lowerOrigin.includes('agenda')) return 'meetings';
  if (lowerOrigin.includes('message') || lowerOrigin.includes('chat')) return 'messages';
  if (lowerOrigin.includes('system') || lowerOrigin.includes('config')) return 'system';
  if (lowerOrigin.includes('marketing') || lowerOrigin.includes('promo')) return 'marketing';
  if (lowerOrigin.includes('update') || lowerOrigin.includes('version')) return 'updates';
  if (lowerOrigin.includes('security') || lowerOrigin.includes('auth')) return 'security';

  return 'system';
};

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const { t } = useTranslation('toast');
  const userId = useUserId();
  const { lastMessage, isConnected, sendMessage } = useWebSocket();
  const queryClient = useQueryClient();
  const { preferences, isDNDActive } = useNotificationPreferences(userId ?? undefined);

  // Local state for WebSocket notifications
  const [wsNotifications, setWsNotifications] = useState<Notification[]>([]);

  // Fetch notifications using React Query
  const {
    data: fetchedNotifications = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['notifications', userId],
    queryFn: async () => {
      const response = await apiNotifications.get<NotificationsApiResponse>(
        `/v1/notifications/user/${userId}?size=50&page=1`
      );
      return response.data.items || [];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: true,
  });

  // Convert error to string format for compatibility
  const error = queryError ? t('error.operation', { action: t('actions.load'), entity: t('entities.notifications') }) : null;

  // Combine fetched + websocket notifications
  const notifications = useMemo(() => {
    const allNotifications = [...wsNotifications, ...fetchedNotifications];
    // Deduplicate by id
    const uniqueMap = new Map(allNotifications.map(n => [n.id, n]));
    return Array.from(uniqueMap.values());
  }, [fetchedNotifications, wsNotifications]);

  // Handle real-time notifications via WebSocket
  useEffect(() => {
    if (!isConnected || !lastMessage) return;

    setWsNotifications(prev => {
      const exists = prev.some(n => n.id === lastMessage.id);
      if (exists) return prev;
      return [lastMessage, ...prev];
    });
  }, [isConnected, lastMessage]);

  // Mark notifications as read (supports single ID or array of IDs)
  const markAsRead = useCallback(
    async (ids: string | string[]) => {
      const notificationIds = Array.isArray(ids) ? ids : [ids];

      if (notificationIds.length === 0) return;

      // Optimistic update - Update local state immediately
      setWsNotifications(prev =>
        prev.map(n => (notificationIds.includes(n.id) ? { ...n, is_read: true } : n))
      );
      queryClient.setQueryData(['notifications', userId], (old: Notification[] | undefined) =>
        old?.map(n => (notificationIds.includes(n.id) ? { ...n, is_read: true } : n))
      );

      // Persist to backend via HTTP API
      try {
        await apiNotifications.post('/v1/notifications/mark-as-read', {
          notification_ids: notificationIds,
        });
      } catch {
        // Fallback: try WebSocket if HTTP fails
        if (isConnected) {
          sendMessage({
            action: 'MARK_AS_READ',
            data: { notification_ids: notificationIds },
          });
        }
      }
    },
    [isConnected, sendMessage, queryClient, userId]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const notificationIds = notifications.filter(n => !n.is_read).map(n => n.id);

    if (notificationIds.length === 0) return;

    // Optimistic update - Update local state immediately
    setWsNotifications(prev => prev.map(notification => ({ ...notification, is_read: true })));
    queryClient.setQueryData(['notifications', userId], (old: Notification[] | undefined) =>
      old?.map(notification => ({ ...notification, is_read: true }))
    );

    // Persist to backend via HTTP API
    try {
      await apiNotifications.post('/v1/notifications/mark-as-read', {
        notification_ids: notificationIds,
      });
    } catch {
      // Fallback: try WebSocket if HTTP fails
      if (isConnected) {
        sendMessage({
          action: 'MARK_AS_READ',
          data: { notification_ids: notificationIds },
        });
      }
    }
  }, [notifications, isConnected, sendMessage, queryClient, userId]);

  // Mark notifications as seen (supports single ID or array of IDs)
  const markAsSeen = useCallback(
    async (ids: string | string[]) => {
      const notificationIds = Array.isArray(ids) ? ids : [ids];

      if (notificationIds.length === 0) return;

      // Optimistic update - Update local state immediately
      setWsNotifications(prev =>
        prev.map(n => (notificationIds.includes(n.id) ? { ...n, seen: true } : n))
      );
      queryClient.setQueryData(['notifications', userId], (old: Notification[] | undefined) =>
        old?.map(n => (notificationIds.includes(n.id) ? { ...n, seen: true } : n))
      );

      // Persist to backend via HTTP API
      try {
        await apiNotifications.post('/v1/notifications/mark-as-seen', {
          notification_ids: notificationIds,
        });
      } catch {
        // Fallback: try WebSocket if HTTP fails
        if (isConnected) {
          sendMessage({
            action: 'MARK_AS_SEEN',
            data: { notification_ids: notificationIds },
          });
        }
      }
    },
    [isConnected, sendMessage, queryClient, userId]
  );

  // Mark all notifications as seen
  const markAllAsSeen = useCallback(async () => {
    const notificationIds = notifications.filter(n => !n.seen).map(n => n.id);

    if (notificationIds.length === 0) return;

    // Optimistic update - Update local state immediately
    setWsNotifications(prev => prev.map(notification => ({ ...notification, seen: true })));
    queryClient.setQueryData(['notifications', userId], (old: Notification[] | undefined) =>
      old?.map(notification => ({ ...notification, seen: true }))
    );

    // Persist to backend via HTTP API
    try {
      await apiNotifications.post('/v1/notifications/mark-as-seen', {
        notification_ids: notificationIds,
      });
    } catch {
      // Fallback: try WebSocket if HTTP fails
      if (isConnected) {
        sendMessage({
          action: 'MARK_AS_SEEN',
          data: { notification_ids: notificationIds },
        });
      }
    }
  }, [notifications, isConnected, sendMessage, queryClient, userId]);

  // Count unread notifications
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.is_read).length;
  }, [notifications]);

  // Count unseen notifications
  const unseenCount = useMemo(() => {
    return notifications.filter(n => !n.seen).length;
  }, [notifications]);

  // Filter function based on user preferences
  const shouldShowNotification = useCallback(
    (notification: Notification): boolean => {
      // If preferences disabled or DND active, still show in list but could be used for sound/toast
      if (!preferences.enabled) return true; // Show all when disabled (no filtering)

      const category = getCategoryFromOrigin(notification.origin);
      if (category && preferences.categories[category]) {
        const categoryPrefs = preferences.categories[category];
        if (!categoryPrefs.enabled) return false;

        // Check priority threshold
        const priorityOrder: NotificationPriority[] = ['low', 'medium', 'high', 'critical'];
        const notificationPriorityIndex = priorityOrder.indexOf(notification.priority || 'low');
        const thresholdIndex = priorityOrder.indexOf(categoryPrefs.priority_threshold);

        if (notificationPriorityIndex < thresholdIndex) return false;
      }

      return true;
    },
    [preferences]
  );

  // Filter notifications based on user preferences (for display purposes)
  const filteredNotifications = useMemo(() => {
    return notifications.filter(shouldShowNotification);
  }, [notifications, shouldShowNotification]);

  // Group notifications by date (using filtered notifications)
  const groupedNotifications = useMemo((): GroupedNotifications => {
    const groups: GroupedNotifications = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    // Sort by date descending first
    const sorted = [...filteredNotifications].sort(
      (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
    );

    sorted.forEach(notification => {
      const sentDate = new Date(notification.sent_at);

      if (isToday(sentDate)) {
        groups.today.push(notification);
      } else if (isYesterday(sentDate)) {
        groups.yesterday.push(notification);
      } else if (isThisWeek(sentDate)) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  }, [filteredNotifications]);

  const contextValue: NotificationsContextProps = {
    notifications,
    groupedNotifications,
    markAsRead,
    markAllAsRead,
    markAsSeen,
    markAllAsSeen,
    unreadCount,
    unseenCount,
    loading,
    error,
    refetch,
    isDNDActive,
  };

  return (
    <NotificationsContext.Provider value={contextValue}>{children}</NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextProps => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export default NotificationsContext;
